import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Eyebrow, Icon } from '../../../ui/index.js';
import { computeDraftAggregates } from '../draftAggregates.js';
import { formatVolume, formatWeight } from '../format.js';
import { useRequestsStore } from '../store.js';
import { Card } from './DraftEditorPrimitives.js';
import { DraftItemEditRow } from './DraftItemEditRow.js';
import { DraftPhotoPreviewsProvider, useDraftPhotoPreviews } from './DraftPhotoPreviews.js';
import type { DraftScratchpad } from '../types.js';

export interface DraftItemsCardProps {
  d: DraftScratchpad;
  persistedId: string | null;
  itemUploading: Record<string, boolean>;
  itemUploadError: Record<string, string>;
  onUploadPhoto: (itemId: string, file: File) => Promise<void>;
  // WHY (#1451): SIMPLE multi-photo add. Same upload pipeline as
  //   `onUploadPhoto` but appends instead of replacing. Resolves to the
  //   appended `{ objectKey }` (or `null` on no-op) so the strip can bind
  //   the blob preview to the resolved key.
  onAddItemPhoto: (itemId: string, file: File) => Promise<{ objectKey: string } | null>;
}

export function DraftItemsCard({
  d,
  persistedId,
  itemUploading,
  itemUploadError,
  onUploadPhoto,
  onAddItemPhoto,
}: DraftItemsCardProps) {
  const { t } = useTranslation(['requests']);
  const addItem = useRequestsStore((s) => s.addDraftItem);
  const patchItem = useRequestsStore((s) => s.patchDraftItem);
  const removeItem = useRequestsStore((s) => s.removeDraftItem);
  const duplicateItem = useRequestsStore((s) => s.duplicateDraftItem);
  const setItemPhoto = useRequestsStore((s) => s.setDraftItemPhoto);
  const removeItemPhoto = useRequestsStore((s) => s.removeDraftItemPhoto);
  const [lastAddedId, setLastAddedId] = useState<string | null>(null);

  const { aggWeight, aggVolume } = computeDraftAggregates(d);

  return (
    <DraftPhotoPreviewsProvider>
      <PhotoPreviewReconciler items={d.items} />
      <Card padded={false}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 16px',
            borderBottom: '1px solid var(--border-2)',
          }}
        >
          <Eyebrow>{t('requests:draft.sections.items', { count: d.items.length })}</Eyebrow>
          <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>
            {t('requests:common.weight')}:{' '}
            <b
              style={{
                color: 'var(--fg-2)',
                fontWeight: 500,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatWeight(aggWeight)}
            </b>
            <span style={{ margin: '0 8px', color: 'var(--fg-4)' }}>·</span>
            {t('requests:common.volume')}:{' '}
            <b
              style={{
                color: 'var(--fg-2)',
                fontWeight: 500,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatVolume(aggVolume)}
            </b>
          </span>
          <div style={{ flex: 1 }} />
          {/* WHY (#701): the duplicate add-box affordance was removed
            from this header — boxes are now added exclusively from
            DraftBoxesSection so CONTAINER-mode drafts (which hide that
            section entirely) have no path to create a box. */}
          <Button
            kind="secondary"
            size="sm"
            icon="plus"
            onClick={() => {
              const id = addItem();
              setLastAddedId(id);
            }}
          >
            {t('requests:draft.addItem')}
          </Button>
        </div>

        {!d.items.length ? (
          <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--fg-3)' }}>
            <Icon name="package-open" size={22} style={{ color: 'var(--fg-4)' }} />
            <div style={{ fontSize: 13, marginTop: 6 }}>{t('requests:draft.emptyItems')}</div>
            <div style={{ marginTop: 10 }}>
              <Button
                kind="secondary"
                size="sm"
                icon="plus"
                onClick={() => {
                  const id = addItem();
                  setLastAddedId(id);
                }}
              >
                {t('requests:draft.addItem')}
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 10, padding: 12 }}>
            {d.items.map((it) => (
              <DraftItemEditRow
                key={it.id}
                item={it}
                uploading={itemUploading[it.id] ?? false}
                uploadError={itemUploadError[it.id] ?? null}
                frozen={false}
                persistedRequestId={persistedId}
                kind={d.kind}
                onPatch={(p) => patchItem(it.id, p)}
                onUploadPhoto={(file) => onUploadPhoto(it.id, file)}
                onClearPhoto={() => setItemPhoto(it.id, null)}
                onAddPhoto={(file) => onAddItemPhoto(it.id, file)}
                onRemovePhoto={(key) => removeItemPhoto(it.id, key)}
                onDuplicate={() => duplicateItem(it.id)}
                onRemove={() => removeItem(it.id)}
                autoFocusName={it.id === lastAddedId}
              />
            ))}
            <div>
              <Button
                kind="secondary"
                size="sm"
                icon="plus"
                data-testid="draft-items-add-bottom"
                onClick={() => {
                  const id = addItem();
                  setLastAddedId(id);
                }}
              >
                {t('requests:draft.addItem')}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </DraftPhotoPreviewsProvider>
  );
}

// Reconciles the shared blob registry against the union of EVERY
// position's photo keys whenever that union changes. Lives inside the
// provider since it needs the registry API. activeKeys is deduplicated
// across positions so a key shared by source + clone survives until
// neither references it.
function PhotoPreviewReconciler({ items }: { items: DraftScratchpad['items'] }) {
  const { reconcile } = useDraftPhotoPreviews();
  const activeKeys = useMemo(() => {
    const unique = new Set<string>();
    for (const it of items) for (const key of it.photos) unique.add(key);
    return [...unique].sort();
  }, [items]);
  // WHY: a JSON-serialized sorted-unique array is a collision-safe effect
  //   signature; a delimiter join would alias keys whose concatenation
  //   matches across different sets.
  // WHY: depend on the stable serialized `signature`, not the array
  //   identity, so the effect fires exactly when the photo union changes.
  //   `activeKeys` is derived deterministically from the same data.
  const signature = JSON.stringify(activeKeys);
  const activeKeysRef = useRef(activeKeys);
  activeKeysRef.current = activeKeys;
  useEffect(() => {
    reconcile(activeKeysRef.current);
  }, [signature, reconcile]);
  return null;
}
